import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoyaltyBadge from "@/components/LoyaltyBadge";
import { Loader2, RefreshCw, LogOut, Phone, Hash, Clock, AlertTriangle, Gift } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import CheckoutReceipt from "@/components/CheckoutReceipt";
import BalanceDuePanel from "@/components/BalanceDuePanel";
import TermsProofDialog from "@/components/TermsProofDialog";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function CheckOutModal({ open, onClose, spot, session, lot, onDone }) {
  const [loading, setLoading] = useState(null);
  const [carrier, setCarrier] = useState({ mcNumber: "", dotNumber: "", licNumber: "" });
  const [applyFree, setApplyFree] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [balanceDays, setBalanceDays] = useState(0);
  const [collectedNow, setCollectedNow] = useState(0);

  useEffect(() => {
    if (session) {
      const c = session.customer || {};
      setApplyFree(false);
      setCarrier({
        mcNumber: session.mcNumber || c.mcNumber || "",
        dotNumber: session.dotNumber || c.dotNumber || "",
        licNumber: session.licNumber || c.licNumber || "",
      });
    }
  }, [session]);

  if (!session) return null;

  const customer = session.customer || {};
  const taxPct = lot?.salesTaxPercent == null ? 0 : Number(lot.salesTaxPercent);
  const base = session.baseRate ?? 0;
  const freeAvail = (customer.loyalty?.availableFreeNights || 0) > 0;
  const prepaid = (session.paymentChannel === "terminal" || session.prepaidAtBooking) && session.paymentStatus === "Paid";
  const effBase = prepaid || (applyFree && freeAvail) ? 0 : base;
  const tax = Math.round(effBase * taxPct) / 100;
  const total = effBase + tax;
  const isOverstay = session.status === "OverstayPendingCharge";
  const expired = new Date(session.expiresAt) < new Date();
  const setC = (k, v) => setCarrier((p) => ({ ...p, [k]: v }));

  const action = async (kind, opts = {}) => {
    setLoading(kind);
    try {
      if (kind === "checkout") {
        const payload = Object.fromEntries(
          Object.entries(carrier).filter(([, v]) => v && v.trim() !== "")
        );
        payload.applyFreeNight = applyFree && freeAvail;
        if (opts.writeOff) payload.writeOffBalance = true;
        const { data } = await api.post(`/sessions/${session.id}/checkout`, payload);
        if (data.freeNightApplied) toast.success("Free night applied — $0 base");
        toast.success(data.prepaid ? "Checked out — already paid" : `Captured $${data.captured.toFixed(2)} via ${data.method}`);
        if (data.loyalty?.pointsAwarded) toast.success(`+${data.loyalty.pointsAwarded} loyalty points earned`);
        onDone();
        setReceipt(data);
        return;
      } else {
        const { data } = await api.post(`/sessions/${session.id}/renew`, {});
        toast.success(`Renewed +24h — charged $${data.charged.toFixed(2)}`);
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-slate-900 text-slate-100 sm:max-w-[520px]">
        {receipt ? (
          <CheckoutReceipt receipt={receipt} spot={spot} lot={lot} customer={customer} onClose={onClose} />
        ) : (<>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-heading text-2xl font-bold">
            Spot {spot?.spotNumber}
            <Badge className={session.tenancyType === "Monthly" ? "bg-blue-500" : "bg-red-500"}>
              {session.tenancyType}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {customer.name} {customer.plate ? `· ${customer.plate}` : ""}
          </DialogDescription>
          {session.ticketNumber && (
            <p data-testid="checkout-ticket" className="font-data text-sm font-bold text-[#d946ef]">
              Ticket #{session.ticketNumber}
            </p>
          )}
          <div className="pt-1"><TermsProofDialog sessionId={session.id} /></div>
        </DialogHeader>

        {customer.loyalty && <LoyaltyBadge loyalty={customer.loyalty} />}

        {isOverstay && (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-950/40 p-3 text-red-300">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm font-semibold">
              Overstay — an additional 24h was auto-charged to the method on file.
            </span>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-4 text-sm">
          <Row icon={Phone} label="Phone" value={customer.phone} />
          <Row icon={Hash} label="Plate" value={customer.plate || "—"} />
          <Row icon={Clock} label="Checked in" value={fmt(session.checkInAt)} />
          <Row
            icon={Clock}
            label="Expires"
            value={fmt(session.expiresAt)}
            valueClass={expired ? "text-red-400" : "text-slate-200"}
          />
          <Row icon={Hash} label="Method" value={`${session.paymentMethod} (${session.paymentStatus})`} />
          {session.companyName && <Row icon={Hash} label="Company" value={session.companyName} />}
          {session.truckType && <Row icon={Hash} label="Truck type" value={session.truckType} />}
          {session.trailerType && <Row icon={Hash} label="Trailer type" value={session.trailerType} />}
        </div>

        {/* Carrier identifiers — editable, saved to session + profile */}
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
          <Label className="text-slate-300">Carrier IDs (optional)</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div>
              <span className="mb-1 block text-xs text-slate-500">MC #</span>
              <Input
                data-testid="checkout-mc"
                value={carrier.mcNumber}
                onChange={(e) => setC("mcNumber", e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                placeholder="MC-123456"
                className="h-11 border-slate-700 bg-slate-900 font-data"
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-slate-500">DOT #</span>
              <Input
                data-testid="checkout-dot"
                value={carrier.dotNumber}
                onChange={(e) => setC("dotNumber", e.target.value.replace(/\D/g, ""))}
                placeholder="1234567"
                className="h-11 border-slate-700 bg-slate-900 font-data"
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-slate-500">Lic #</span>
              <Input
                data-testid="checkout-lic"
                value={carrier.licNumber}
                onChange={(e) => setC("licNumber", e.target.value.toUpperCase())}
                placeholder="X12345"
                className="h-11 border-slate-700 bg-slate-900 font-data"
              />
            </div>
          </div>
        </div>

        {prepaid && (
          <div data-testid="checkout-prepaid" className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200">
            Paid so far: <b>${((session.amountCharged || 0) + collectedNow).toFixed(2)}</b>
            {session.cardLast4 ? ` · Card ****${session.cardLast4}` : ""}
            {session.approvalCode ? ` · Approval ${session.approvalCode}` : ""}
            {session.openEnded ? " · Open-ended (auto-charges every 25h)" : ""}.
            {collectedNow > 0 ? ` Balance of $${collectedNow.toFixed(2)} collected now.` : " No charge at checkout"} — auto-charges stop.
            {(session.autoCharges || []).map((c) => (
              <div key={c.cycle} data-testid={`checkout-autocharge-${c.cycle}`} className="mt-1 font-data text-xs">
                #{c.cycle} {c.referenceId} · {c.status} · ${Number(c.amount || 0).toFixed(2)}{c.approvalCode ? ` · ${c.approvalCode}` : ""}
                {c.status !== "approved" ? ` · ${c.message}` : ""}
              </div>
            ))}
          </div>
        )}
        {session.paymentFailed && collectedNow === 0 && (
          <div data-testid="checkout-payment-failed" className="rounded-lg border border-amber-500/50 bg-amber-950/40 px-4 py-3 text-sm font-semibold text-amber-200">
            Payment failed — needs attention. Last auto-charge: {session.lastAutoCharge?.message}. Retrying next cycle.
          </div>
        )}
        {prepaid && (
          <BalanceDuePanel sessionId={session.id} onBalance={setBalanceDays} onCollected={(amt) => setCollectedNow((v) => v + amt)} onWriteOff={() => action("checkout", { writeOff: true })} />
        )}

        {freeAvail && !prepaid && (
          <label className="flex cursor-pointer items-center justify-between rounded-lg border border-[#A0148C]/50 bg-[#A0148C]/10 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <Gift className="h-4 w-4 text-[#A0148C]" /> Apply free night ({customer.loyalty.availableFreeNights} available)
            </span>
            <input
              data-testid="checkout-apply-free"
              type="checkbox"
              checked={applyFree}
              onChange={(e) => setApplyFree(e.target.checked)}
              className="h-5 w-5 accent-[#A0148C]"
            />
          </label>
        )}

        {!prepaid && <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 font-data text-sm">
          <div className="flex justify-between text-slate-400">
            <span>Base rate</span>
            <span>
              {applyFree && freeAvail ? (
                <>
                  <span className="mr-2 text-slate-600 line-through">${base.toFixed(2)}</span>
                  <span className="text-emerald-400">$0.00</span>
                </>
              ) : (
                `$${base.toFixed(2)}`
              )}
            </span>
          </div>
          {applyFree && freeAvail && (
            <div className="flex justify-between text-emerald-400">
              <span>Free night</span>
              <span>-${base.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-400">
            <span data-testid="checkout-tax-label">Sales tax ({taxPct}%)</span>
            <span data-testid="checkout-tax-amount">${tax.toFixed(2)}</span>
          </div>
          <div className="mt-2 flex justify-between border-t border-slate-800 pt-2 text-base font-bold text-slate-100">
            <span>Capture now</span>
            <span data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
        </div>}

        <div className={`grid gap-3 ${prepaid ? "grid-cols-1" : "grid-cols-2"}`}>
          {!prepaid && <Button
            data-testid="checkout-renew"
            variant="secondary"
            onClick={() => action("renew")}
            disabled={loading !== null}
            className="h-14 gap-2 bg-slate-800 text-base font-semibold hover:bg-slate-700"
          >
            {loading === "renew" ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
            Renew +24h
          </Button>}
          <Button
            data-testid="checkout-submit"
            onClick={() => action("checkout")}
            disabled={loading !== null || balanceDays > 0}
            className="h-14 gap-2 bg-[#A0148C] text-base font-bold hover:bg-[#80106F]"
          >
            {loading === "checkout" ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />}
            Check Out
          </Button>
        </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value, valueClass = "text-slate-200" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" /> {label}
      </span>
      <span className={`font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}
